import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, navigate } from '@reach/router';
import Text from 'components/Text';
import { BlueButton, BlueBorderButton } from 'components/Button';
import Break from 'components/Break';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { editAccountInfo } from 'reducers/auth/actions';
import {
  isEmailValid,
  isLengthValid,
  isNoSpecialCharactersValid,
  isPasswordValid,
  newPasswordsMatch
} from 'utils/validate';
import {
  COMPASS_SERVER_ERROR_MESSAGES,
  EMAIL_MAX_LENGTH,
  ERROR_MESSAGES,
  PASSWORD_MAX_LENGTH,
  SPECIAL_CHARACTERS_ALLOWED,
  USER_NAME_MAX_LENGTH
} from 'utils/appConstants';
import { useDispatch, useSelector } from 'react-redux';

const EditAccountPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  position: relative;
`;

const AccountInfoPanel = styled.form`
  width: 575px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
`;

const AccountInfoRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 8px 0px;
`;

const InfoType = styled(Text)`
  font-size: 12px;
  width: 50%;
  color: #4a4a4a;
  margin: auto 0px;
`;

const InfoTypeSubtext = styled.span`
  font-size: 10px;
  display: inline-block;
  padding: 4px 0px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 30px;
  line-height: 30px;
  padding: 0.2em 0.5em;
  font-family: Crossten;
  font-size: 16px;
  border: ${({ isError }) =>
    isError ? '1px solid #EA6E6D' : '1px solid #d8d8d8'};
  color: #0d151f;
`;

const InputErrorText = styled(Text)`
  color: #ea6e6d;
  font-size: 14px;
  margin: 12px;
`;

const OPEN_SUCCESS_MODAL = 'OPEN_SUCCESS_MODAL';
const CLICK_SUCCESS_BUTTON = 'CLICK_SUCCESS_BUTTON';
const OPEN_FAIL_MODAL = 'OPEN_FAIL_MODAL';
const CLICK_FAIL_BUTTON = 'CLICK_FAIL_BUTTON';

const defaultPasswordStates = {
  confirmNewPassword: '',
  newPassword: '',
  oldPassword: ''
};
const defaultInputErrors = {
  firstName: [],
  lastName: [],
  email: [],
  confirmNewPassword: [],
  newPassword: [],
  oldPassword: []
};

const EditAccount = () => {
  const dispatch = useDispatch();
  const authenticatedUser = useSelector(state => state.auth.authenticatedUser);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [inputErrors, setInputErrors] = useState(defaultInputErrors);
  const [modalState, setModalState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const { firstName, lastName, email } = authenticatedUser;
    setFirstName(firstName);
    setLastName(lastName);
    setEmail(email);
  }, []);

  const updateModalState = (event, state) => {
    if (event) {
      event.preventDefault();
    }

    if (state === CLICK_SUCCESS_BUTTON) {
      return navigate('/account');
    }
    if (state === CLICK_FAIL_BUTTON) {
      return setModalState(null);
    }

    setModalState(state);
  };

  const handleInputChange = (event, validationObject) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    const { SPECIAL_CHARACTERS_ERROR, MAX_LENGTH_ERROR } = ERROR_MESSAGES;

    let errorMessages = [];
    if (validationObject) {
      if (
        validationObject.pattern &&
        !isNoSpecialCharactersValid(value, validationObject.pattern)
      ) {
        errorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (
        validationObject.maxLength &&
        !isLengthValid(value, validationObject.maxLength)
      ) {
        errorMessages.push(MAX_LENGTH_ERROR(validationObject.maxLength));
      }

      if (errorMessages.length) {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: errorMessages
        }));
      } else {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: []
        }));
      }
    }
    if (name === 'firstName') {
      setFirstName(value);
    } else if (name === 'lastName') {
      setLastName(value);
    } else if (name === 'email') {
      setEmail(value);
    } else if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'confirmNewPassword') {
      setConfirmNewPassword(value);
    } else if (name === 'oldPassword') {
      setOldPassword(value);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSubmitting(true);

    const {
      EMAIL_ADDRESS_IN_USE_ERROR,
      OLD_AND_NEW_PASSWORD_REQUIRED,
      OLD_PASSWORD_NOT_CORRECT
    } = COMPASS_SERVER_ERROR_MESSAGES;

    const isValid = validateSubmission();
    if (!isValid) {
      return setIsSubmitting(false);
    }
    const requestBody = {
      firstName,
      lastName,
      email
    };
    if (newPassword && confirmNewPassword && oldPassword) {
      requestBody.password = newPassword;
      requestBody.oldPassword = oldPassword;
    }

    try {
      await dispatch(editAccountInfo(requestBody));
      updateModalState(null, OPEN_SUCCESS_MODAL);
    } catch (err) {
      const errorState = {};

      // These messages come directly from compass-server
      if (err.message === EMAIL_ADDRESS_IN_USE_ERROR) {
        errorState.email = [EMAIL_ADDRESS_IN_USE_ERROR];
      } else if (err.message === OLD_AND_NEW_PASSWORD_REQUIRED) {
        errorState.newPassword = ['Old and new password are required'];
      } else if (err.message === OLD_PASSWORD_NOT_CORRECT) {
        errorState.oldPassword = ['Old password is incorrect'];
      }
      // Set inputs and errors back to default state
      setInputErrors({
        ...defaultInputErrors,
        ...errorState
      });
      setConfirmNewPassword(defaultPasswordStates.confirmNewPassword);
      setNewPassword(defaultPasswordStates.newPassword);
      setOldPassword(defaultPasswordStates.oldPassword);
      setModalState(null);
      updateModalState(null, OPEN_FAIL_MODAL);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateSubmission = () => {
    const {
      MAX_LENGTH_ERROR,
      EMAIL_MAX_LENGTH_ERROR,
      SPECIAL_CHARACTERS_ERROR,
      INVALID_EMAIL_ERROR,
      INVALID_PASSWORD,
      PASSWORDS_MUST_MATCH,
      OLD_PASSWORD_REQUIRED
    } = ERROR_MESSAGES;
    let isValid = true;

    const nameErrorMessages = [];
    const verifyName = (value, name) => {
      if (!isLengthValid(value, USER_NAME_MAX_LENGTH)) {
        nameErrorMessages.push(MAX_LENGTH_ERROR(USER_NAME_MAX_LENGTH));
      }
      if (!isNoSpecialCharactersValid(value)) {
        nameErrorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (nameErrorMessages.length) {
        isValid = false;
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: nameErrorMessages
        }));
      }
    };
    verifyName(firstName, 'firstName');
    verifyName(lastName, 'lastName');

    //email
    const emailErrorMessages = [];
    if (!isEmailValid(email)) {
      emailErrorMessages.push(INVALID_EMAIL_ERROR);
    }
    if (!isLengthValid(email, EMAIL_MAX_LENGTH)) {
      emailErrorMessages.push(EMAIL_MAX_LENGTH_ERROR);
    }
    if (emailErrorMessages.length) {
      isValid = false;
      setInputErrors(prevInputErrors => ({
        ...prevInputErrors,
        email: emailErrorMessages
      }));
    }

    //password
    const oldPasswordErrorMessages = [];
    const newPasswordErrorMessages = [];
    const confirmNewPasswordErrorMessages = [];
    if (oldPassword || newPassword || confirmNewPassword) {
      //Do not validate old password with regular password validation because
      //the old password might not have been created using the same validation requirements
      if (!oldPassword) {
        oldPasswordErrorMessages.push(OLD_PASSWORD_REQUIRED);
      }
      if (!isPasswordValid(newPassword)) {
        newPasswordErrorMessages.push(INVALID_PASSWORD);
      }
      if (!newPasswordsMatch(newPassword, confirmNewPassword)) {
        confirmNewPasswordErrorMessages.push(PASSWORDS_MUST_MATCH);
      }
    }
    if (
      oldPasswordErrorMessages.length ||
      newPasswordErrorMessages.length ||
      confirmNewPasswordErrorMessages.length
    ) {
      isValid = false;
      setInputErrors(prevInputErrors => ({
        ...prevInputErrors,
        oldPassword: oldPasswordErrorMessages,
        newPassword: newPasswordErrorMessages,
        confirmNewPassword: confirmNewPasswordErrorMessages
      }));
    }

    return isValid;
  };

  const determineIfPasswordFieldsAreRequired = () => {
    if (newPassword || confirmNewPassword || oldPassword) {
      return true;
    }
  };

  const fieldHasErrors = field => {
    if (Array.isArray(inputErrors[field]) && inputErrors[field].length > 0) {
      return true;
    }
  };

  return (
    <EditAccountPage>
      <AccountInfoPanel onSubmit={handleSubmit} autoComplete="off">
        <Text margin="20px 0px">Account Information</Text>
        <AccountInfoRow>
          <InfoType>First Name</InfoType>
          <TextInput
            required
            type="text"
            name="firstName"
            onChange={e =>
              handleInputChange(e, {
                maxLength: USER_NAME_MAX_LENGTH,
                pattern: SPECIAL_CHARACTERS_ALLOWED
              })
            }
            isError={inputErrors.firstName.length}
            value={firstName}
            maxLength={USER_NAME_MAX_LENGTH}
            pattern={SPECIAL_CHARACTERS_ALLOWED}
          />
        </AccountInfoRow>
        {fieldHasErrors('firstName') &&
          inputErrors.firstName.map((error, i) => (
            <InputErrorText key={i}>{error}</InputErrorText>
          ))}

        <AccountInfoRow>
          <InfoType>Last Name</InfoType>
          <TextInput
            required
            type="text"
            name="lastName"
            onChange={e =>
              handleInputChange(e, {
                maxLength: USER_NAME_MAX_LENGTH,
                pattern: SPECIAL_CHARACTERS_ALLOWED
              })
            }
            isError={inputErrors.lastName.length}
            value={lastName}
            maxLength={USER_NAME_MAX_LENGTH}
            pattern={SPECIAL_CHARACTERS_ALLOWED}
          />
        </AccountInfoRow>
        {fieldHasErrors('lastName') &&
          inputErrors.lastName.map((error, i) => (
            <InputErrorText key={i}>{error}</InputErrorText>
          ))}

        <AccountInfoRow>
          <InfoType>Email Address</InfoType>
          <TextInput
            required
            type="email"
            name="email"
            onChange={e =>
              handleInputChange(e, { maxLength: EMAIL_MAX_LENGTH })
            }
            isError={inputErrors.email.length}
            maxLength={EMAIL_MAX_LENGTH}
            value={email}
          />
        </AccountInfoRow>
        {fieldHasErrors('email') &&
          inputErrors.email.map((error, i) => (
            <Text color="#EA6E6D" fontSize="14px" margin="12px" key={i}>
              {error}
            </Text>
          ))}

        <Break margin="16px 0px" />

        <AccountInfoRow>
          <InfoType>Old Password</InfoType>
          <TextInput
            name="oldPassword"
            type="password"
            onChange={e => handleInputChange(e)}
            required={determineIfPasswordFieldsAreRequired}
            isError={inputErrors.oldPassword.length}
            value={oldPassword}
            autoComplete="off"
          />
        </AccountInfoRow>
        {fieldHasErrors('oldPassword') &&
          inputErrors.oldPassword.map((error, i) => (
            <Text color="#EA6E6D" fontSize="14px" margin="12px" key={i}>
              {error}
            </Text>
          ))}
        <AccountInfoRow>
          <InfoType>
            New Password
            <InfoTypeSubtext>
              • Must be longer than 8 characters
            </InfoTypeSubtext>
            <InfoTypeSubtext>
              • Must include at least 1 number and 1 special character
            </InfoTypeSubtext>
          </InfoType>
          <TextInput
            name="newPassword"
            type="password"
            onChange={e =>
              handleInputChange(e, { maxLength: PASSWORD_MAX_LENGTH })
            }
            required={confirmNewPassword ? true : false}
            isError={inputErrors.newPassword.length}
            maxLength="50"
            value={newPassword}
            autoComplete="new-password"
          />
        </AccountInfoRow>
        {fieldHasErrors('newPassword') &&
          inputErrors.newPassword.map((error, i) => (
            <InputErrorText key={i}>{error}</InputErrorText>
          ))}
        <AccountInfoRow>
          <InfoType>Confirm New Password</InfoType>
          <TextInput
            name="confirmNewPassword"
            type="password"
            onChange={e =>
              handleInputChange(e, { maxLength: PASSWORD_MAX_LENGTH })
            }
            required={newPassword ? true : false}
            isError={inputErrors.confirmNewPassword.length}
            maxLength="50"
            value={confirmNewPassword}
            autoComplete="off"
          />
        </AccountInfoRow>
        {fieldHasErrors('confirmNewPassword') &&
          inputErrors.confirmNewPassword.map((error, i) => (
            <InputErrorText key={i}>{error}</InputErrorText>
          ))}

        <Break margin="12px 0px" />

        <AccountInfoRow style={{ flexWrap: 'wrap' }}>
          <Link to="/account">
            <BlueBorderButton type="button">Cancel</BlueBorderButton>
          </Link>
          <BlueButton
            margin="0px 0px 0px 12px"
            buttonWidth="105px"
            type="submit"
            disabled={isSubmitting}
          >
            Save Changes
          </BlueButton>
        </AccountInfoRow>
      </AccountInfoPanel>

      {/* Modal Conditional Rendering */}
      {modalState === OPEN_SUCCESS_MODAL && (
        <ConfirmationModal
          updateModalState={e => updateModalState(e, CLICK_SUCCESS_BUTTON)}
          header="Account Information"
          message="Your Account Information has successfully been updated"
        />
      )}
      {modalState === OPEN_FAIL_MODAL && (
        <ConfirmationModal
          updateModalState={e => updateModalState(e, CLICK_FAIL_BUTTON)}
          header="Account Information"
          message="Error occurred while updating your Account Information. Please try again."
        />
      )}
    </EditAccountPage>
  );
};

export default EditAccount;
